import React, { useMemo, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { DamageAnimation } from './DamageAnimation';
import { useDamageAnimationContext } from './DamageAnimationContext';

// Fallback slot coordinates (cell-based) - only used when DOM lookup fails
const slotCoordinates = {
  'opponent_front_0': { cellX: 4, cellY: 10 },
  'opponent_front_1': { cellX: 14, cellY: 10 },
  'opponent_front_2': { cellX: 24, cellY: 9 },
  'opponent_front_3': { cellX: 32, cellY: 10 },
  'opponent_front_4': { cellX: 42, cellY: 9 },
  'opponent_flank_0': { cellX: 52, cellY: 10 },
  'opponent_flank_1': { cellX: 52, cellY: 11 },
  'opponent_flank_2': { cellX: 70, cellY: 10 },
  'opponent_flank_3': { cellX: 79, cellY: 10 },
  'opponent_flank_4': { cellX: 86, cellY: 10 },
  'your_front_0': { cellX: 3, cellY: 18 },
  'your_front_1': { cellX: 15, cellY: 18 },
  'your_front_2': { cellX: 22, cellY: 18 },
  'your_front_3': { cellX: 32, cellY: 17 },
  'your_front_4': { cellX: 39, cellY: 18 },
  'your_flank_0': { cellX: 52, cellY: 18 },
  'your_flank_1': { cellX: 62, cellY: 18 },
  'your_flank_2': { cellX: 69, cellY: 17 },
  'your_flank_3': { cellX: 81, cellY: 18 },
  'your_flank_4': { cellX: 89, cellY: 17 },
};

const GRID_CELL_SIZE = 20;

const getSlotPixelPositionFallback = (zoneName, index) => {
  const slotKey = `${zoneName}_${index}`;
  const coords = slotCoordinates[slotKey];
  if (!coords) return null;
  return {
    x: (coords.cellX * GRID_CELL_SIZE) + (GRID_CELL_SIZE / 2),
    y: (coords.cellY * GRID_CELL_SIZE) + (GRID_CELL_SIZE / 2)
  };
};

/**
 * Component to render death animations in an empty slot.
 * Uses DOM lookup for correct viewport position (empty slots have data-slot-zone / data-slot-index).
 * Falls back to grid coordinates only if DOM element is not found.
 */
export const SlotDeathAnimations = ({ zoneName, index }) => {
  const { getSlotAnimations } = useDamageAnimationContext();
  const slotAnims = getSlotAnimations(zoneName, index);
  const fallbackPosition = useMemo(() => getSlotPixelPositionFallback(zoneName, index), [zoneName, index]);
  const [position, setPosition] = useState(fallbackPosition);

  useLayoutEffect(() => {
    if (!slotAnims || slotAnims.length === 0) return;
    const el = document.querySelector(`[data-slot-zone="${zoneName}"][data-slot-index="${index}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      setPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      });
    } else if (fallbackPosition) {
      setPosition(fallbackPosition);
    }
  }, [zoneName, index, slotAnims, fallbackPosition]);

  if (!slotAnims || slotAnims.length === 0 || !position) {
    return null;
  }

  return createPortal(
    <div
      key={`${zoneName}_slot_anim_${index}`}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -50%)',
        width: '192px',
        height: '192px',
        backgroundColor: 'transparent',
        overflow: 'visible',
        zIndex: 10000,
        pointerEvents: 'none',
      }}
    >
      {slotAnims.map((anim) => (
        <DamageAnimation
          key={anim.key}
          damageAmount={anim.damageAmount}
          onComplete={anim.onComplete}
          animationKey={anim.key}
        />
      ))}
    </div>,
    document.body
  );
};

